import React, { useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { useAdminGetSegmentById } from '../../hooks/admin-get-segment-by-id.hook';
import { useUpdateSegment } from '../../hooks/update-segment.hook';
import { useArchiveSegment } from '../../hooks/archive-segment.hook';
import { useRestoreSegment } from '../../hooks/restore-segment.hook';
import { Segment } from '../../types/segment.type';
import { ADMIN_SEGMENTS_APP_PATHS } from '../../constants/segments-app-paths.constants';
import {
  SEGMENT_DETAILS_CONFIRM_DIALOG_CONTENT,
  SEGMENT_DETAILS_TABS,
  resolveSegmentDetailsTab,
} from '../../constants/segment-details.constants';
import SegmentInformationsTab from '../../components/Containers/SegmentInformationsTab.component';
import { SegmentDetailsHeader } from '../../components/Containers/SegmentDetailsHeader.component';
import { SegmentInvoicesTab } from '../../../invoices/components/Containers/SegmentInvoicesTab.component';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';
import { NotFoundState } from '../../../shared/components/States/NotFoundState.component';
import TabsController from '../../../shared/components/Tabs/ButtonTabsController';

type DialogState = { kind: 'none' } | { kind: 'confirm'; action: 'archive' | 'restore' };

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '1rem',
  },
});

export default function AdminSegmentDetailsView() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { segmentId } = useParams<{ segmentId: string }>();

  const currentTab = resolveSegmentDetailsTab(new URLSearchParams(location.search).get('tab'));

  const { isLoading, segment, refetch } = useAdminGetSegmentById({ segmentId });
  const { updateSegment, isSubmitting: isUpdating } = useUpdateSegment();
  const { archiveSegment, isSubmitting: isArchiving } = useArchiveSegment();
  const { restoreSegment, isSubmitting: isRestoring } = useRestoreSegment();

  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const closeDialog = () => setDialog({ kind: 'none' });

  const handleTabChange = (tab: string) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', tab);
    history.push({ search: newParams.toString() });
  };

  const handleSegmentUpdated = async (editedFields: Partial<Segment>) => {
    if (isLoading || !segment) return;

    const updatedSegment = await updateSegment({ segmentId: segment.id, segmentData: editedFields });
    if (!updatedSegment) return;

    await refetch();
  };

  const handleConfirm = async () => {
    if (dialog.kind !== 'confirm' || !segment) return;

    const result = dialog.action === 'archive' ? await archiveSegment(segment.id) : await restoreSegment(segment.id);
    if (!result) return;

    closeDialog();
    await refetch();
  };

  return (
    <section className={classes.root}>
      {isLoading && <Skeleton variant="rect" animation="wave" height={92} />}
      {!isLoading && !segment && (
        <NotFoundState
          title="Segment introuvable"
          backLabel="Retour aux segments"
          backTo={ADMIN_SEGMENTS_APP_PATHS.SEGMENTS_LIST}
        />
      )}
      {!isLoading && segment && (
        <React.Fragment>
          <SegmentDetailsHeader
            segment={segment}
            onArchiveActionClick={() =>
              setDialog({ kind: 'confirm', action: segment.archivedAt ? 'restore' : 'archive' })
            }
          />
          <TabsController
            size="small"
            tabs={SEGMENT_DETAILS_TABS}
            currentTabId={currentTab}
            onTabChange={handleTabChange}
          />
          {currentTab === 'informations' && (
            <SegmentInformationsTab segment={segment} isUpdating={isUpdating} updateSegment={handleSegmentUpdated} />
          )}
          {currentTab === 'invoicing' && <SegmentInvoicesTab segmentId={segment.id} />}
        </React.Fragment>
      )}
      {dialog.kind === 'confirm' && segment && (
        <ConfirmActionDialog
          open
          title={SEGMENT_DETAILS_CONFIRM_DIALOG_CONTENT[dialog.action].title}
          description={SEGMENT_DETAILS_CONFIRM_DIALOG_CONTENT[dialog.action].describe(segment.name)}
          confirmLabel={SEGMENT_DETAILS_CONFIRM_DIALOG_CONTENT[dialog.action].confirmLabel}
          isSubmitting={isArchiving || isRestoring}
          onConfirm={handleConfirm}
          onClose={closeDialog}
        />
      )}
    </section>
  );
}
